import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useInfiniteQuery } from "@tanstack/react-query";
import { selectRole } from "src/redux/features/user/selectors/userState.selectors";
import { IRoles } from "src/utils";
import { useDebounce } from "@components/shared/hooks/useDebounce";
import { useOptionalVirtualAssistant } from "@components/virtual-receptionist/context/VirtualAssistantContext";
import { useBulkResolveContacts } from "@components/virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import { searchMessages } from "@components/Messages/actions/searchMessages.actions";
import { searchSmsMessages } from "@components/virtual-receptionist/sms/actions/searchSmsMessages.actions";

interface SourceEntity {
  type: string;
  uid?: string;
  id?: number | string;
}

export function useGlobalMessageSearch() {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 400);
  const trimmedQuery = debouncedQuery.trim();
  const isEnabled = trimmedQuery.length > 0;

  const currentRole = useSelector(selectRole);
  const { uid, workforces } = useSelector((state: any) => state.userState);
  const activeCompanyId = useSelector((state: any) => state.userState.activeCompanyId);
  const sessionNumber = useOptionalVirtualAssistant()?.sessionNumber || "";
  const isSmsEnabled = isEnabled && Boolean(sessionNumber);

  const myIdentities = useMemo<SourceEntity[]>(() => {
    if (currentRole === IRoles.Employee) {
      return (workforces ?? []).map((workforce: any) => ({ type: "workforce", uid: workforce.uid }));
    }
    return [{ type: "user", uid }];
  }, [currentRole, workforces, uid]);

  const isMine = (entity?: SourceEntity | null) =>
    Boolean(entity) && myIdentities.some((identity) => identity.type === entity?.type && String(identity.uid) === String(entity?.uid));

  const internalQuery = useInfiniteQuery({
    queryKey: ["global-search", "internal", trimmedQuery, activeCompanyId],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await searchMessages({ q: trimmedQuery, page: pageParam, pageSize: 20 });
      return res?.data;
    },
    getNextPageParam: (lastPage: any) => {
      const page = Number(lastPage?.page);
      const total = Number(lastPage?.totalPages);
      if (!page || !total || page >= total) return undefined;
      return page + 1;
    },
    initialPageParam: 1,
    enabled: isEnabled,
  });

  const smsQuery = useInfiniteQuery({
    queryKey: ["global-search", "sms", trimmedQuery, activeCompanyId, sessionNumber],
    queryFn: async ({ pageParam = 1 }) => {
      return searchSmsMessages({ q: trimmedQuery, fromNumber: sessionNumber, companyId: activeCompanyId, pageParam, limit: 20 });
    },
    getNextPageParam: (lastPage) => (lastPage?.meta?.hasNextPage ? lastPage.meta.page + 1 : undefined),
    initialPageParam: 1,
    enabled: isSmsEnabled,
  });

  const internalResults = useMemo(
    () => internalQuery.data?.pages.flatMap((page: any) => page?.items ?? []) ?? [],
    [internalQuery.data],
  );

  const smsResults = useMemo(() => smsQuery.data?.pages.flatMap((page) => page?.data ?? []) ?? [], [smsQuery.data]);

  const smsContactPhones = useMemo(() => {
    const phones = smsResults.map((message: any) => (message.direction === "outgoing" ? message.to : message.from));
    return Array.from(new Set(phones.filter(Boolean)));
  }, [smsResults]);

  const { data: smsBulkResolvedMap = {} } = useBulkResolveContacts(smsContactPhones);

  return {
    query,
    setQuery,
    trimmedQuery,
    isEnabled,
    isMine,
    internal: {
      results: internalResults,
      isLoading: internalQuery.isLoading,
      isFetchingNextPage: internalQuery.isFetchingNextPage,
      hasNextPage: internalQuery.hasNextPage,
      fetchNextPage: internalQuery.fetchNextPage,
      error: internalQuery.error,
    },
    sms: {
      results: smsResults,
      isLoading: smsQuery.isLoading,
      isFetchingNextPage: smsQuery.isFetchingNextPage,
      hasNextPage: smsQuery.hasNextPage,
      fetchNextPage: smsQuery.fetchNextPage,
      error: smsQuery.error,
      bulkResolvedMap: smsBulkResolvedMap,
    },
  };
}
